import { getBrandName } from '../brandModule/brandUtils';
import { chapterNames } from '../configuration/constants';

import { removeSplashAndStyles, hideSplashAndStyles } from './splashAndStyles';

namespace NavigationModule {
    export interface LifecycleOptions {
        persist?: boolean;
    }
    export interface Lifecycle {
        onChapterWillLoad: (
            callback: () => void,
            params: LifecycleOptions,
        ) => void;
        onChapterDidLoad: (
            callback: () => void,
            params: LifecycleOptions,
        ) => void;
    }
}

namespace SplashModule {
    export type ChapterName = string;

    export interface Params {
        lifecycle: NavigationModule.Lifecycle;
        currentChapter: () => ChapterName | { chapterName: ChapterName };
        scrollToTop: () => void;
        maxSplashScreenVisibilityTime: null | number;
    }
}

const createSplashModule = ({
    lifecycle,
    currentChapter,
    scrollToTop,
    maxSplashScreenVisibilityTime,
}: SplashModule.Params) => ({
    removeSplashScreen: removeSplashAndStyles,
    hideSplashScreen: hideSplashAndStyles,
    run: function () {
        const getSplashes = (): HTMLElement[] =>
            Array.from(document.querySelectorAll('body > .bootstrap-splash'));
        const allSplashes = getSplashes();

        const brand = getBrandName();

        const currentSplash = allSplashes.find(
            splash => splash.dataset.brand === brand,
        );

        if (currentSplash) {
            currentSplash.style.display = 'block';
            allSplashes
                .filter(splash => splash.dataset.brand !== brand)
                .forEach(splashElement => splashElement.remove());
        }

        let firstLoad = true;

        const showSecondarySplash = (shouldShow: boolean) => {
            const [, secondarySplashElement] = getSplashes();

            if (!secondarySplashElement) {
                return;
            }

            secondarySplashElement.style.display = shouldShow
                ? 'block'
                : 'none';

            if (shouldShow) {
                // locking the body
                document.body.style.overflow = 'hidden';
                scrollToTop();
                return;
            }
            // unlocking the body
            document.body.style.overflow = 'visible';
        };

        const handleChapterWillLoad = () => {
            let chapterName = currentChapter();

            if (typeof chapterName === 'object') {
                chapterName = chapterName.chapterName;
            }

            if (chapterName === 'error') {
                getSplashes().map(removeSplashAndStyles);
                return;
            }

            if (firstLoad) {
                // not displaying spinner on first load
                return;
            }

            showSecondarySplash(true);
        };

        const hideSplash = () => {
            const [primarySplashElement] = getSplashes();
            showSecondarySplash(false);
            if (maxSplashScreenVisibilityTime) {
                setTimeout(function () {
                    removeSplashAndStyles(primarySplashElement);
                }, maxSplashScreenVisibilityTime);
                return;
            }
            removeSplashAndStyles(primarySplashElement);
            firstLoad = false;
        };

        const handleChapterDidLoad = () => {
            let chapterName = currentChapter();
            if (typeof chapterName === 'object') {
                chapterName = chapterName.chapterName;
            }

            if (chapterName !== chapterNames.CATALOG) {
                hideSplash();
            }
        };

        const handleHideSplashEvent = () => {
            hideSplash();
        };

        showSecondarySplash(false);

        lifecycle.onChapterWillLoad(handleChapterWillLoad, { persist: true });
        lifecycle.onChapterDidLoad(handleChapterDidLoad, {
            persist: true,
        });
        document.addEventListener('hideBootstrapSplashScreen', () => {
            handleHideSplashEvent();
        });
    },
});

export { createSplashModule };
